/**
 * fadeAnimation.js allows for easy fading-in of elements on a page
 *
 * Usage:
 *  1. Add classes "animation" and "fade-in" and "-fade-up" to each element in the page that needs to be animated
 *  2. In page-level JS file, grab those elements with `querySelectorAll` and pass into a new instance of fadeAnimation();
 *  3. In page-level SCSS file, for the page-scope (e.g. `.page--pricing{}`) run @include enable-fade-animation();
 * Done!
 **/

import getElementPosition from '../components/getElementPosition'
import throttle from 'lodash/throttle'

export default class FadeAnimation {
  constructor(animatedElements) {
    this.elementHeightMap = []
    this.windowHeight = $(window).height()

    this.initializeHeightMap(animatedElements)
    this.attachEvent()

    // Initial set up
    this.detectElementsInViewport(true)
  }

  // Generates an array of mappings like so: { el: HTMLElement, pos: Int representing position, hasBeenAnimated}
  initializeHeightMap(animatedElements) {
    animatedElements.forEach((element) => {
      const elementPosition = getElementPosition(element).top
      let elementItem = {
        el: element,
        pos: elementPosition,
        hasBeenAnimated: false,
      }
      // If the element is in view at page load, it means it's above the fold, so load in a cascade
      elementItem.aboveTheFold = elementPosition < this.windowHeight

      // STORE IT
      this.elementHeightMap.push(elementItem)
    })
  }

  // Attaches scroll event listener
  attachEvent() {
    // Listen to scroll
    window.addEventListener(
      'scroll',

      // Throttle the number of times that detectElementsInViewport can fire (every 50ms)
      throttle((e) => {
        // Pass in 'false' as an optimization to skip part of algorithm that applies animation to ATF elements
        this.detectElementsInViewport(false)
      }, 130),
    )
  }

  // Detects if an element is in the current viewport
  detectElementsInViewport(initialCheck) {
    // The point (vertically) that is used as the comparison point against elements' vertical position
    const currentScrollPoint = window.scrollY + this.windowHeight * 0.9

    // If it's the initial check, animate above-the-fold (ATF) elements
    if (initialCheck) {
      const aboveTheFoldElements = this.elementHeightMap.filter(
        (elementData) => elementData.aboveTheFold,
      )

      // Animate ATF elements in a cascade
      aboveTheFoldElements.forEach((elementData, index) => {
        // This value is in MS
        const fadeOffsetConstantMS = 150

        // Amount of time between each ATF element fading in
        const fadeInDelay = index * fadeOffsetConstantMS

        // Apply timeout functions to add the class to the element, fading them into view
        setTimeout(() => {
          elementData.el.classList.add('animation-done')
        }, fadeInDelay)
      })

      // Filter out the ATF elements
      this.elementHeightMap = this.elementHeightMap.filter(
        (elementData) => !elementData.aboveTheFold,
      )
    }

    // Iterate through to find which elements should be animated
    this.elementHeightMap.forEach((elementHeightPair, index) => {
      // If element has scrolled into view, animate it.
      if (currentScrollPoint > elementHeightPair.pos) {
        elementHeightPair.el.classList.add('animation-done')

        // Mark current element for removal from array
        elementHeightPair.hasBeenAnimated = true
      }
    })

    // Optimization: Filters out already-processed animations
    this.elementHeightMap = this.elementHeightMap.filter(
      (elementHeightPair) => !elementHeightPair.hasBeenAnimated,
    )
  }
}
